import { useEffect, useMemo } from 'react'
import { getVariantFromProduct, IProduct } from '@core/api/Products/types'
import Attachments from '@core/components/ProductDetail/Attachments'
import Awards from '@core/components/ProductDetail/Awards'
import BrandInfo from '@core/components/ProductDetail/BrandInfo'
import HazardWarnings from '@core/components/ProductDetail/HazardWarnings'
import Price from '@core/components/ProductDetail/Price'
import ProductDescription from '@core/components/ProductDetail/ProductDescription'
import ProductDetailCategoryBreadcrumbs from '@core/components/ProductDetail/ProductDetailCategoryBreadcrumbs'
import ProductImage from '@core/components/ProductDetail/ProductImage'
import ProductInformation from '@core/components/ProductDetail/ProductInformation'
import ProductVariationSwitch from '@core/components/ProductDetail/ProductVariationSwitch'
import SimpleAttributesTable from '@core/components/ProductDetail/SimpleAttributes'
import { useServices } from '@core/utils/ioc'
import { ProductContextProvider } from '@core/utils/models/product/ProductContext'
import { LYSNewBadge } from '../Primitives/LYSBadge/LYSBadge'
import DiscontinuedHint from './DiscontinuedHint'
import ProductBundle from './ProductBundle'
import CrossSellProductRelations from '@core/components/ProductDetail/ProductRelations/CrossSellProductRelations'
import FollowUpProductRelations from '@core/components/ProductDetail/ProductRelations/FollowUpProductRelations'
import LastVisitedPDPsRecommendations from '@core/components/ProductDetail/ProductRecommendations/LastVisitedPDPsRecommendations'
import useEvents from '../AutoSuggest/useEvents'
import ProductImageWithZoom from '@core/components/ProductDetail/ProductImageWithZoom'
import PackagingUnitSwitch from '../shared/ProductPackagingUnitSwitch'
import SparePartsProductRelations from './ProductRelations/SparePartsProductRelations'

interface Props {
  product: IProduct
  variantId: string
}

interface StaticProps {
  ProductImage: typeof ProductImage
  ProductImageWithZoom: typeof ProductImage
  Awards: typeof Awards
  HazardWarnings: typeof HazardWarnings
  ProductInformation: typeof ProductInformation
  Attachments: typeof Attachments
  Price: typeof Price
  PackagingUnitSwitch: typeof PackagingUnitSwitch
  SimpleAttributes: typeof SimpleAttributesTable
  BrandInfo: typeof BrandInfo
  ProductDescription: typeof ProductDescription
  ProductVariationSwitch: typeof ProductVariationSwitch
  ProductDetailCategoryBreadcrumbs: typeof ProductDetailCategoryBreadcrumbs
  ProductBundle: typeof ProductBundle
  DiscontinuedHint: typeof DiscontinuedHint
  FollowUpProductRelations: typeof FollowUpProductRelations
  CrossSellProductRelations: typeof CrossSellProductRelations
  SparePartsProductRelations: typeof SparePartsProductRelations
  LastVisitedPDPsRecommendations: typeof LastVisitedPDPsRecommendations
  NewBadge: typeof LYSNewBadge
}

const ProductDetail: React.FC<Props> & StaticProps = ({
  product,
  children,
  variantId,
}) => {
  const { productContextService } = useServices()
  const selectedVariant = getVariantFromProduct(product, variantId)
  const { postVariantViewedEvent } = useEvents()

  useEffect(() => {
    postVariantViewedEvent(variantId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantId])

  const productContext = useMemo(
    () =>
      selectedVariant
        ? productContextService.getProductDetailContext(
            product,
            selectedVariant
          )
        : null,
    [productContextService, product, selectedVariant]
  )

  if (!productContext) return null

  return (
    <ProductContextProvider productContext={productContext}>
      {children}
    </ProductContextProvider>
  )
}

ProductDetail.ProductImage = ProductImage
ProductDetail.ProductImageWithZoom = ProductImageWithZoom
ProductDetail.Awards = Awards
ProductDetail.HazardWarnings = HazardWarnings
ProductDetail.ProductInformation = ProductInformation
ProductDetail.Attachments = Attachments
ProductDetail.Price = Price
ProductDetail.PackagingUnitSwitch = PackagingUnitSwitch
ProductDetail.SimpleAttributes = SimpleAttributesTable
ProductDetail.BrandInfo = BrandInfo
ProductDetail.ProductDescription = ProductDescription
ProductDetail.ProductVariationSwitch = ProductVariationSwitch
ProductDetail.ProductDetailCategoryBreadcrumbs =
  ProductDetailCategoryBreadcrumbs
ProductDetail.ProductBundle = ProductBundle
ProductDetail.DiscontinuedHint = DiscontinuedHint
ProductDetail.FollowUpProductRelations = FollowUpProductRelations
ProductDetail.CrossSellProductRelations = CrossSellProductRelations
ProductDetail.SparePartsProductRelations = SparePartsProductRelations
ProductDetail.NewBadge = LYSNewBadge
ProductDetail.LastVisitedPDPsRecommendations = LastVisitedPDPsRecommendations

export default ProductDetail
